import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import type { WorkOrderViewModel } from '@api/enterprise/openapi/models';
import { formatCompactDate } from './WorkOrderHeader/utils';
import { CustomerReviewButton } from './CustomerReviewButton';
import { LockedStatusChip } from './LockedStatusChip';
import { useLayoutContext } from '@components/Layout';
import { isWorkOrderLocked } from '@utils/workOrderHelpers';

type Props = {
  workOrder: WorkOrderViewModel;
  visible: boolean;
};

export const CondensedWorkOrderHeader = ({ workOrder, visible }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { sidebarWidth } = useLayoutContext();

  if (!visible) return null;

  const dueDateDisplay =
    formatCompactDate(workOrder.expectedDueDate) || 'No Due Date';

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 48,
        left: { xs: 0, sm: sidebarWidth },
        right: 0,
        zIndex: 1100,
        backgroundColor: 'background.paper',
        boxShadow: 2,
        borderBottom: 1,
        borderColor: 'divider',
        px: { xs: 1.5, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        transition: 'left 0.2s'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 0.25 : 2,
          minWidth: 0,
          flex: 1
        }}
      >
        <Typography
          variant={isMobile ? 'subtitle2' : 'subtitle1'}
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            flexShrink: 0
          }}
        >
          {workOrder.jobNumber}
        </Typography>

        <Typography
          variant={isMobile ? 'caption' : 'body2'}
          sx={{
            color: theme.palette.text.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0
          }}
        >
          {workOrder.customerName || 'No Customer'}
        </Typography>

        <Typography
          variant={isMobile ? 'caption' : 'body2'}
          sx={{
            color: theme.palette.text.secondary,
            flexShrink: 0
          }}
        >
          {dueDateDisplay}
        </Typography>
      </Box>

      <Box
        sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}
      >
        <CustomerReviewButton label={isMobile ? 'Review' : 'Customer Review'} />
        {isWorkOrderLocked(workOrder.status) && <LockedStatusChip />}
      </Box>
    </Box>
  );
};
